/**
 * CreativeDetailSheet — Right-side panel showing creative preview and actions.
 *
 * Uses ShadCN Sheet component. Shows preview, config summary, share link,
 * and actions: Use in proposal, Generate alternative, Archive.
 */
import React, { useEffect, useState } from 'react';
import {
  Play, Copy, RefreshCw, Archive, FileText, Edit2, CopyPlus, Trash2,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { CREATIVE_STATUSES, PROPOSAL_STATUSES } from '@/components/deal/mockDealData';
import useAuthenticatedMediaUrl from '@/hooks/useAuthenticatedMediaUrl';
import { videoApiClient } from '@/services/videoApi';
import CreativePreviewFallback from '@/components/deal/CreativePreviewFallback';

function normalizePreviewMediaUrl(url) {
  if (typeof url !== 'string' || !url) return '';
  if (
    url.startsWith('http://')
    || url.startsWith('https://')
    || url.startsWith('blob:')
    || url.startsWith('data:')
  ) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${videoApiClient.baseUrl}${url}`;
  }
  return url;
}

export default function CreativeDetailSheet({
  creative,
  open,
  onOpenChange,
  onGenerateAlternative,
  onArchive,
  onRenameCreative,
  onDeleteDraft,
  onUseInProposal,
  onEdit,
  onDuplicate,
  linkedProposals = [],
}) {
  if (!creative) return null;

  const status = CREATIVE_STATUSES[creative.status] || CREATIVE_STATUSES.draft;
  const previewAsset = creative.assets?.find((asset) => asset.type === 'video' && asset.url) || creative.assets?.[0] || null;
  const previewVideoUrl = normalizePreviewMediaUrl(
    previewAsset?.url || creative.videoUrl || creative.previewUrl || '',
  );
  const previewPoster = normalizePreviewMediaUrl(
    creative.thumbnailUrl || previewAsset?.thumbnail_url || '',
  );
  const {
    resolvedUrl: playablePreviewVideoUrl,
    isLoading: isLoadingPreviewVideo,
  } = useAuthenticatedMediaUrl(previewVideoUrl);
  const [posterFailed, setPosterFailed] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const hasVideoPreview = Boolean(playablePreviewVideoUrl);
  const canDeleteDraft = Boolean(onDeleteDraft) && creative.status === 'draft' && !creative.selectedOnWorkspace;
  const date = creative.createdAt
    ? new Date(creative.createdAt).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
      })
    : '';

  useEffect(() => {
    setPosterFailed(false);
  }, [previewPoster]);

  useEffect(() => {
    setRenameDraft(creative.creativeName || creative.displayName || '');
    setIsRenaming(false);
    setIsSavingName(false);
    setIsDeletingDraft(false);
  }, [creative.creativeName, creative.displayName, creative.id]);

  const handleCopyLink = () => {
    if (creative.shareUrl) {
      navigator.clipboard?.writeText(creative.shareUrl);
      toast.success('Preview link copied');
    }
  };

  const handleRenameSubmit = async () => {
    const nextName = renameDraft.trim();
    if (!nextName) {
      toast.error('Enter a creative name.');
      return;
    }
    if (nextName === (creative.creativeName || creative.displayName || '').trim()) {
      setIsRenaming(false);
      return;
    }

    try {
      setIsSavingName(true);
      await onRenameCreative?.(creative, nextName);
      setIsRenaming(false);
    } catch {
      // parent handler already surfaces the error
    } finally {
      setIsSavingName(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!canDeleteDraft) return;

    const confirmed = window.confirm(`Delete draft "${creative.displayName || 'creative'}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      setIsDeletingDraft(true);
      await onDeleteDraft?.(creative);
      onOpenChange(false);
    } catch {
      // parent handler already surfaces the error
    } finally {
      setIsDeletingDraft(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Creative Details</SheetTitle>
          <SheetDescription>
            Preview and manage this creative asset.
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 space-y-5">
          {/* Preview */}
          <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden relative">
            {hasVideoPreview ? (
              <video
                src={playablePreviewVideoUrl}
                poster={previewPoster}
                controls
                playsInline
                className="h-full w-full object-cover"
              />
            ) : previewPoster && !posterFailed ? (
              <img
                src={previewPoster}
                alt="Creative preview"
                className="w-full h-full object-cover opacity-80"
                onError={() => setPosterFailed(true)}
              />
            ) : (
              <CreativePreviewFallback
                label={creative.status === 'processing' ? 'Preview rendering' : 'Preview unavailable'}
              />
            )}
            {isLoadingPreviewVideo && !hasVideoPreview ? (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-950/60 backdrop-blur-[1px]">
                <div className="flex items-center gap-2 rounded-full bg-white/92 px-4 py-2 text-sm font-medium text-slate-950 shadow-sm">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Loading preview
                </div>
              </div>
            ) : null}
            {!hasVideoPreview && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                  <Play className="h-5 w-5 text-coral ml-0.5" />
                </div>
              </div>
            )}
          </div>

          {/* Status + format */}
          <div className="flex flex-wrap gap-2">
            <Badge className={`${status.className} border-0`}>
              {status.label}
            </Badge>
            {creative.format && (
              <Badge variant="outline">
                {creative.format === 'ctv_16_9' ? 'TV (16:9)' :
                  creative.format === 'social_9_16' ? 'Social (9:16)' : 'Social (1:1)'}
              </Badge>
            )}
            <Badge variant="outline">
              {creative.duration ? `${creative.duration}s` : '15s'}
            </Badge>
          </div>

          <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-slate-50/60 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {isRenaming ? (
                  <Input
                    value={renameDraft}
                    onChange={(event) => setRenameDraft(event.target.value)}
                    maxLength={255}
                    className="h-10 bg-white"
                    autoFocus
                  />
                ) : (
                  <h3 className="truncate text-lg font-semibold text-slate-950">
                    {creative.displayName || 'Spec Ad'}
                  </h3>
                )}
                <p className="mt-1 text-sm text-slate-500">
                  {creative.creativeName ? 'Saved creative name' : 'Using fallback name until this creative is renamed.'}
                </p>
              </div>
              {onRenameCreative ? (
                <div className="flex shrink-0 gap-2">
                  {isRenaming ? (
                    <>
                      <Button
                        size="sm"
                        className="h-9 rounded-xl bg-slate-950 text-white hover:bg-slate-800"
                        onClick={handleRenameSubmit}
                        disabled={isSavingName}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-9 rounded-xl bg-white"
                        onClick={() => {
                          setRenameDraft(creative.creativeName || creative.displayName || '');
                          setIsRenaming(false);
                        }}
                        disabled={isSavingName}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 rounded-xl bg-white"
                      onClick={() => setIsRenaming(true)}
                    >
                      <Edit2 className="h-4 w-4" />
                      Rename
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {/* Config summary */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Objective</span>
              <span
                className="text-foreground font-medium"
                title={
                  creative.objective === 'awareness' ? 'A general "this business exists, here’s what it offers" message — the safest default.' :
                    creative.objective === 'seasonal' ? 'Tied to a season, holiday, or limited-time event.' :
                      creative.objective === 'grand_opening' ? 'Announces a new location or relaunch.' :
                        ''
                }
              >
                {creative.objective === 'awareness' ? 'Brand Awareness' :
                  creative.objective === 'seasonal' ? 'Seasonal Campaign' :
                    creative.objective === 'grand_opening' ? 'Grand Opening' :
                      creative.objective || 'General'}
              </span>
            </div>
            {creative.tone && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tone</span>
                <span className="text-foreground font-medium capitalize">{creative.tone}</span>
              </div>
            )}
            {date && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span className="text-foreground">{date}</span>
              </div>
            )}
          </div>

          {/* Share link */}
          {creative.shareUrl && (
            <Button variant="outline" size="sm" onClick={handleCopyLink} className="w-full">
              <Copy className="h-4 w-4" />
              Copy preview link
            </Button>
          )}

          {/* Linked proposals */}
          {linkedProposals.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Used in {linkedProposals.length} proposal{linkedProposals.length !== 1 ? 's' : ''}
                </h4>
                <div className="space-y-1.5">
                  {linkedProposals.map((p) => {
                    const pStatus = PROPOSAL_STATUSES[p.status] || PROPOSAL_STATUSES.draft;
                    return (
                      <div key={p.id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                        <span className="text-sm text-foreground truncate">
                          {p.packageName || 'Proposal'}
                        </span>
                        <Badge className={`${pStatus.className} border-0 text-[10px] px-1.5 py-0`}>
                          {pStatus.label}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        <SheetFooter className="flex-col gap-2">
          {onUseInProposal && (
            <Button
              className="w-full bg-coral hover:bg-coral/90 text-white"
              onClick={() => {
                onUseInProposal(creative);
                onOpenChange(false);
              }}
            >
              <FileText className="h-4 w-4" />
              Use in proposal
            </Button>
          )}
          <div className="grid grid-cols-2 gap-2 w-full">
            {onEdit && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  onEdit(creative);
                  onOpenChange(false);
                }}
              >
                <Edit2 className="h-4 w-4" />
                Edit
              </Button>
            )}
            {onDuplicate && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  onDuplicate(creative);
                  onOpenChange(false);
                }}
              >
                <CopyPlus className="h-4 w-4" />
                Duplicate
              </Button>
            )}
          </div>
          {onGenerateAlternative && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                onGenerateAlternative(creative);
                onOpenChange(false);
              }}
            >
              <RefreshCw className="h-4 w-4" />
              Generate alternative
            </Button>
          )}
          {canDeleteDraft ? (
            <Button
              variant="ghost"
              className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={handleDeleteDraft}
              disabled={isDeletingDraft}
            >
              <Trash2 className="h-4 w-4" />
              Delete draft
            </Button>
          ) : null}
          {onArchive && creative.status !== 'archived' && (
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => {
                onArchive(creative);
                onOpenChange(false);
              }}
            >
              <Archive className="h-4 w-4" />
              Archive
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
